import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, toUser } from '../../store/authStore';
import { authApi } from '../../services/api';
import Wordmark from '../../components/Wordmark';

function messageFor(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
  const status = e.response?.status;
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (status === 401) return 'That email and password do not match. Check both and try again.';
  if (status === 423) return 'This account is locked after too many attempts. Wait a few minutes, then try again.';
  return 'We could not sign you in just now. Check your connection and try again.';
}

export default function Login() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setTokens = useAuthStore((s) => s.setTokens);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await authApi.login(email, password);
      if (data.access_token) setTokens(data.access_token, data.refresh_token);
      setUser(toUser(data.user));
      navigate('/', { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <Wordmark />
        <h1 className="mt-8 text-display text-ink">Sign in</h1>
        <p className="mt-2 text-body text-ink-muted">Use the email and password your MerchantView account was set up with.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
          {error && (
            <div role="alert" className="rounded-md border border-danger bg-danger-tint p-3 text-caption text-danger">
              <span className="font-semibold">Sign in failed. </span>
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="label">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting || !email || !password}>
            {submitting ? 'Signing in' : 'Sign in'}
          </button>
        </form>
      </div>
    </main>
  );
}
